import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { useCart } from "@/lib/cart";
import { useSEO } from "@/lib/seo";
import { trackPurchase } from "@/lib/analytics";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cart = useCart();
  const [state, setState] = useState("polling");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);
  useSEO({ title: "Order Confirmed | Gold Time Jewelry", canonicalPath: "/payment/success", noindex: true });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          trackPurchase(sessionId, data);
          cart.clear();
          localStorage.removeItem("gt_cart_token");
          return;
        }
      } catch {
        if (cancelled) return;
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        setTimeout(poll, 2000);
      } else if (!cancelled) {
        setState("timeout");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page">
      <Header />
      <section className="pt-[164px] pb-24 lg:pt-[196px] lg:pb-32 min-h-[60vh]">
        <div className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          {state === "polling" && (
            <div data-testid="payment-processing">
              <Loader2 size={44} strokeWidth={1} className="mx-auto text-[#C9A227] animate-spin" />
              <h1 className="font-display text-3xl text-neutral-900 mt-6">Confirming your payment...</h1>
              <p className="text-neutral-600 mt-3">This takes a few seconds.</p>
            </div>
          )}

          {state === "paid" && order && (
            <div data-testid="payment-confirmed">
              <CheckCircle2 size={48} strokeWidth={1} className="mx-auto text-[#C9A227]" />
              <h1 className="font-display text-4xl text-neutral-900 mt-6">Thank you — order confirmed</h1>
              <p className="text-neutral-600 mt-3">
                Order <span className="font-semibold" data-testid="payment-order-id">{order.order_id?.slice(0, 8).toUpperCase()}</span> —{" "}
                {money(order.amount)} paid. We're preparing your order now — watch your email for insured tracking, or for your showroom pickup confirmation.
              </p>
              <div className="mt-8 bg-white border border-[#E8DCC4] rounded-sm p-6 text-left space-y-3" data-testid="payment-order-items">
                {order.items.map((i) => (
                  <div key={i.sku} className="flex justify-between text-sm">
                    <span className="text-neutral-700">{i.name} × {i.quantity}</span>
                    <span className="font-semibold text-neutral-900">{money(i.unit_price * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/catalog"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5"
              >
                Keep browsing
              </Link>
            </div>
          )}

          {(state === "error" || state === "timeout") && (
            <div data-testid="payment-error">
              <XCircle size={44} strokeWidth={1} className="mx-auto text-neutral-400" />
              <h1 className="font-display text-3xl text-neutral-900 mt-6">We couldn't confirm your payment</h1>
              <p className="text-neutral-600 mt-3">
                If your card was charged, call us at (346) 307-7864 and we'll sort it out right away.
              </p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
